import { api } from './edge-client';
import { useAuthStore } from '@/stores';
import { refreshAccessToken } from '@/utils/auth';

// ===== Constants from FTTH API documentation =====
export const FTTH_BASE_URL = '';
export const FTTH_CLIENT_APP = '53d57a7f-3f89-4e9d-873b-3d071bc6dd9f';
export const FTTH_AUTH_ENDPOINT = '/api/auth/Contractor/token';

// ===== Token Auth Types =====
export interface FTTHTokenRequest {
  username: string;
  password: string;
  client_id?: string;
  grant_type?: string;
  scope?: string;
}

export interface FTTHTokenResponse {
  access_token: string;
  refresh_token?: string;
  expires_in?: number;
  token_type?: string;
  scope?: string;
}

export interface ApiResponse<T = any> {
  statusCode?: number;
  data?: T;
  message?: string;
  errors?: any[];
  timestamp?: string;
  duration?: string;
  [key: string]: any;
}

// ===== JWT Decoder =====
export function decodeJWT(token: string): Record<string, any> | null {
  try {
    const payload = token.split('.')[1];
    const decoded = atob(payload.replace(/-/g, '+').replace(/_/g, '/'));
    return JSON.parse(decoded);
  } catch {
    return null;
  }
}

// Helper to inject token into data/params
function injectToken(data: any = {}) {
  const { accessToken } = useAuthStore.getState();
  if (accessToken) {
    return { ...data, ftth_token: accessToken };
  }
  return data;
}

// ===== FTTH Token Service (uses AppDeploy Client API / Mock API) =====
export async function fetchFTTHToken(username: string, password: string): Promise<FTTHTokenResponse> {
  try {
    const response = await api.post(FTTH_AUTH_ENDPOINT, {
      client_id: FTTH_CLIENT_APP,
      grant_type: 'password',
      username,
      password,
      scope: 'openid profile',
    });

    const data = response.data;
    if (data && data.access_token) {
      return data;
    }
    throw new Error('لم يتم استلام access_token من الخادم');
  } catch (err: any) {
    const status = err.status || err.statusCode || (err.response && err.response.status);
    
    // If the server explicitly rejected the credentials with 400 or 401, propagate the error
    if (status === 400 || status === 401) {
      throw new Error('اسم المستخدم أو كلمة المرور غير صحيحة');
    }
    
    // Re-throw authentication or connection error without fake token/credential fallback
    throw new Error(err.message || 'اسم المستخدم أو كلمة المرور غير صحيحة');
  }
}

// ===== Wrapper ApiClient to replicate Axios syntax with AppDeploy client api =====
class ApiClientWrapper {
  private refreshPromise: Promise<string | null> | null = null;

  private async request<T = any>(
    method: 'get' | 'post' | 'put' | 'delete',
    url: string,
    data?: any,
    isRetry = false
  ): Promise<{ data: T }> {
    try {
      const { accessToken } = useAuthStore.getState();
      let finalUrl = url;
      let bodyData = data;

      // Prepend /api to relative URLs if missing
      if (!url.startsWith('http://') && !url.startsWith('https://')) {
        const cleanPath = url.startsWith('/') ? url : '/' + url;
        if (!cleanPath.startsWith('/api/') && !cleanPath.startsWith('/ftth-auth/')) {
          finalUrl = '/api' + cleanPath;
        }
      }

      if (method === 'get') {
        const isAbsolute = finalUrl.startsWith('http://') || finalUrl.startsWith('https://');
        const urlObj = new URL(finalUrl, 'http://localhost');
        const params = urlObj.searchParams;
        
        // Merge data parameters into search params
        if (data && typeof data === 'object') {
          for (const [key, val] of Object.entries(data)) {
            if (val !== undefined && val !== null) {
              params.set(key, String(val));
            }
          }
        }
        
        // Inject token
        if (accessToken) {
          params.set('ftth_token', accessToken);
        }
        
        const searchStr = params.toString();
        finalUrl = (isAbsolute ? urlObj.origin : '') + urlObj.pathname + (searchStr ? '?' + searchStr : '');
        bodyData = undefined;
      } else {
        bodyData = injectToken(data);
      }

      const res = await (api as any)[method](finalUrl, bodyData);
      return { data: res.data as T };
    } catch (error: any) {
      const status = error.status || error.statusCode || (error.response && error.response.status);
      const cleanUrl = url.toLowerCase();
      const authState = useAuthStore.getState();
      const isLocalSubUser = authState.user?.authType === 'LOCAL';
      const isLocalAuthEndpoint = cleanUrl.includes('/auth/me') || cleanUrl.includes('/auth/change-password') || cleanUrl.includes('/users') || cleanUrl.includes('/roles') || cleanUrl.includes('/permissions');

      // Attempt single-flight token refresh on 401 (for SSO / portal tokens) if not already a retried request
      if (status === 401 && (!isLocalSubUser || isLocalAuthEndpoint)) {
        if (!isRetry && !isLocalSubUser) {
          if (!this.refreshPromise) {
            this.refreshPromise = refreshAccessToken()
              .catch((err: any) => {
                console.warn('[ApiClient] Refresh token failed:', err);
                return null;
              })
              .finally(() => {
                this.refreshPromise = null;
              });
          }

          const newToken = await this.refreshPromise;
          if (newToken) {
            // Retry the original request exactly once with new token
            return await this.request<T>(method, url, data, true);
          }
        }

        // Only after refresh definitively fails or retry fails, logout cleanly
        console.error('apiClient: Unauthorized or token refresh failed, logging out');
        useAuthStore.getState().logout();
        if (typeof window !== 'undefined') {
          window.location.hash = '#/login';
        }
        throw new Error('Session expired. Please log in again.');
      }

      // Re-throw all other API errors
      throw error;
    }
  }

  async get<T = any>(url: string, params?: Record<string, any>): Promise<{ data: T }> {
    return this.request<T>('get', url, params);
  }

  async post<T = any>(url: string, data?: any, _config?: any): Promise<{ data: T }> {
    return this.request<T>('post', url, data);
  }

  async put<T = any>(url: string, data?: any): Promise<{ data: T }> {
    return this.request<T>('put', url, data);
  }

  async patch<T = any>(url: string, data?: any): Promise<{ data: T }> {
    return this.request<T>('put', url, data);
  }

  async delete<T = any>(url: string): Promise<{ data: T }> {
    return this.request<T>('delete', url);
  }

  async postForm<T = any>(url: string, formData: URLSearchParams): Promise<{ data: T }> {
    const data: Record<string, string> = {};
    formData.forEach((value, key) => {
      data[key] = value;
    });
    return this.post<T>(url, data);
  }
}

export const apiClient = new ApiClientWrapper();
